import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2, RefreshCw, Unplug, MessageSquare, CheckCircle2, XCircle, Send } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "./AdminLayout";

export default function WhatsAppConnection() {
  const [status, setStatus] = useState(null);
  const [qr, setQr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [testMessage, setTestMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const load = async () => {
    try {
      const [connection, code] = await Promise.all([
        api.get("/whatsapp/status"),
        api.get("/whatsapp/qr")
      ]);
      setStatus(connection.data);
      setQr(code.data?.data || code.data?.qr || null);
      setConnectionError("");
    } catch (error) {
      setConnectionError(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, []);

  const action = async (path) => {
    setActionLoading(true);
    try {
      await api.post(path);
      await load();
      toast.success("تم تحديث حالة الاتصال بنجاح");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setActionLoading(false);
    }
  };

  const sendTest = async (event) => {
    event.preventDefault();
    setSending(true);
    try {
      await api.post("/whatsapp/test-message", { phone: testPhone, message: testMessage });
      toast.success("تم إرسال رسالة الاختبار بنجاح");
      setTestMessage("");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <AdminLayout title="ربط واتساب وإرسال OTP" subtitle="مراقبة جلسة واتساب وتفعيل إرسال رموز التحقق والإشعارات للزبائن والسائقين">
      <div className="max-w-3xl space-y-6">
        <Card className="border border-border/80 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-emerald-600" />
                  حالة خدمة WhatsApp
                </CardTitle>
                <CardDescription>الاتصال المباشر لإرسال أكواد الدخول والتحقق</CardDescription>
              </div>
              {status?.status === "connected" ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  متصل وجاهز للعمل
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                  <XCircle className="w-4 h-4 text-amber-600" />
                  {status?.status === "connecting" ? "جاري الاتصال..." : "غير متصل حالياً"}
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                {connectionError && (
                  <div className="p-3.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs leading-relaxed">
                    <strong>تنبيه: </strong> {connectionError}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 rounded-xl bg-muted/40 border">
                  <div>
                    <span className="text-xs text-muted-foreground block">رقم الهاتف المرتبط:</span>
                    <span className="font-mono font-bold text-sm text-foreground mt-0.5 block" dir="ltr">
                      {status?.phone || "غير محدد"}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground block">آخر اتصال مسجل:</span>
                    <span className="font-mono text-xs text-foreground mt-0.5 block">
                      {status?.last_connection ? new Date(status.last_connection).toLocaleString('ar-SY') : "لا يوجد"}
                    </span>
                  </div>
                </div>

                {status?.provider === "cloud" ? (
                  <div className="p-4 rounded-lg bg-blue-50 border border-blue-200 text-blue-900 text-xs leading-relaxed">
                    يعمل الاتصال عبر <strong>WhatsApp Cloud API (Meta)</strong>. لا يتطلب هذا الوضع مسح كود QR؛ بمجرد ضبط مفاتيح Meta في متغيرات البيئة ستعمل رسائل التحقق تلقائياً.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {status?.status !== "connected" && qr && (
                      <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-border rounded-xl bg-card">
                        <p className="text-xs font-semibold text-muted-foreground mb-3">امسح كود QR باستخدام تطبيق WhatsApp من هاتفك</p>
                        <img src={qr} alt="WhatsApp QR Code" className="w-56 h-56 border border-border p-2 bg-white rounded-lg shadow-sm" />
                      </div>
                    )}
                    {status?.status !== "connected" && !qr && (
                      <p className="text-xs text-muted-foreground text-center py-4">
                        رمز QR غير جاهز بعد أو أن الخدمة غير متصلة بالنفق. اضغط على "إعادة الاتصال" للبدء.
                      </p>
                    )}
                    <div className="flex gap-3">
                      <Button onClick={() => action("/whatsapp/connect")} disabled={actionLoading} className="gap-2">
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        إعادة الاتصال
                      </Button>
                      <Button variant="outline" onClick={() => action("/whatsapp/disconnect")} disabled={actionLoading} className="gap-2">
                        <Unplug className="w-4 h-4" />
                        قطع الاتصال
                      </Button>
                    </div>
                  </div>
                )}

                {/* Test Message Section */}
                <div className="border-t pt-5">
                  <h3 className="font-bold text-sm mb-3 flex items-center gap-2">
                    <Send className="w-4 h-4 text-primary" />
                    إرسال رسالة تجريبية
                  </h3>
                  <form onSubmit={sendTest} className="space-y-3">
                    <div>
                      <Label htmlFor="t_phone">رقم الهاتف المستلم (سوري) *</Label>
                      <Input
                        id="t_phone"
                        required
                        value={testPhone}
                        onChange={(e) => setTestPhone(e.target.value)}
                        placeholder="+9639XXXXXXXX"
                        dir="ltr"
                      />
                    </div>
                    <div>
                      <Label htmlFor="t_msg">نص الرسالة *</Label>
                      <Input
                        id="t_msg"
                        required
                        value={testMessage}
                        onChange={(e) => setTestMessage(e.target.value)}
                        placeholder="رسالة اختبارية من منصة كفرنبل أوردر..."
                      />
                    </div>
                    <Button type="submit" disabled={sending || status?.status !== "connected"} className="gap-2">
                      {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                      إرسال الرسالة الآن
                    </Button>
                  </form>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
